import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useRoleWeights,
	useUpdateRoleWeights,
} from '@/api/queries/scoring-queries'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { ROLE_LABELS } from '@/constants/role-labels'
import type { Role } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

function isValidWeight(value: number) {
	return !Number.isNaN(value) && value >= 0 && value <= 10
}

export function RoleWeightsTab() {
	const { t } = useTranslation()
	const { data: weights = [], isLoading } = useRoleWeights()
	const updateMutation = useUpdateRoleWeights()
	// valores digitados, por role; ausência = mantém o peso salvo
	const [drafts, setDrafts] = useState<Partial<Record<Role, string>>>({})

	const changed = weights.filter((w) => {
		const draft = drafts[w.role]
		return (
			draft !== undefined && draft.trim() !== '' && Number(draft) !== w.weight
		)
	})

	const handleSave = async () => {
		const items = changed.map((w) => ({
			role: w.role,
			weight: Number(drafts[w.role]),
		}))
		if (items.some((i) => !isValidWeight(i.weight))) {
			toast.error(t('administration.scoring.weights.invalidWeightError'))
			return
		}

		try {
			await updateMutation.mutateAsync({ items })
			setDrafts({})
			toast.success(t('administration.scoring.weights.saveSuccess'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	if (isLoading) {
		return (
			<div className="p-6 text-center text-sm text-muted-foreground">
				{t('administration.scoring.loading')}
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<p className="text-xs text-muted-foreground sm:flex-1">
					{t('administration.scoring.weights.hint')}
				</p>
				<Button
					type="button"
					onClick={handleSave}
					disabled={updateMutation.isPending || changed.length === 0}
					className="shrink-0 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 sm:w-auto"
				>
					{updateMutation.isPending ? (
						<>
							<Spinner /> {t('administration.scoring.saving')}
						</>
					) : (
						t('administration.scoring.save')
					)}
				</Button>
			</div>

			<div className="rounded-lg border border-kaefer-light-silver">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('administration.scoring.weights.role')}</TableHead>
							<TableHead className="w-32">
								{t('administration.scoring.weights.weight')}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{weights.map((w) => (
							<TableRow key={w.role}>
								<TableCell className="text-kaefer-dark-grey">
									{ROLE_LABELS[w.role] ?? w.role}
								</TableCell>
								<TableCell>
									<Input
										type="number"
										min={0}
										max={10}
										step={0.05}
										value={drafts[w.role] ?? String(w.weight)}
										onChange={(e) =>
											setDrafts((prev) => ({
												...prev,
												[w.role]: e.target.value,
											}))
										}
										className="h-8 w-24"
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
